import {
    ApplicationRef,
    ComponentMirror,
    ComponentRef,
    createComponent,
    DestroyableInjector,
    EnvironmentInjector,
    EventEmitter,
    Injectable,
    Injector,
    isSignal,
    OutputRef,
    Provider,
    reflectComponentType,
} from "@angular/core";
import { jSith } from "../util/jquery-replacement";
import { S25Assert } from "../util/s25.assert";
import { TypeMapService } from "./type.map.service";

export const INJECTOR_TOKEN = "__ANGJS_BRIDGE_INJECTOR__";
export type AngJSScope = {
    $parent?: AngJSScope;
    // $on?(eventName: string, listener: () => void): () => void;
    [INJECTOR_TOKEN]?: Injector;
};

/** The nearest injector an AngularJS controller put on its scope with {@link CompileService.addProvidersToScope}. */
export function findScopeInjector($scope: AngJSScope | null | undefined): Injector | undefined {
    let searchScope = $scope;
    while (searchScope && !searchScope[INJECTOR_TOKEN]) {
        searchScope = searchScope.$parent;
    }
    return searchScope?.[INJECTOR_TOKEN];
}

@Injectable({
    providedIn: "root",
})
export class CompileService {
    constructor(
        private injector: Injector,
        private environmentInjector: EnvironmentInjector,
        private app: ApplicationRef,
        private typeMapService: TypeMapService,
    ) {}

    public static getInstance() {
        return window.angBridge.CompileService;
    }

    /** AngularJS entry point for {@link findScopeInjector} */
    getScopeInjector($scope: AngJSScope | null | undefined): Injector | undefined {
        return findScopeInjector($scope);
    }

    addProvidersToScope($scope: AngJSScope, name: string, providers: Provider[]): DestroyableInjector {
        const injector = Injector.create({
            providers,
            name,
            parent: findScopeInjector($scope) ?? this.injector,
        });
        $scope[INJECTOR_TOKEN] = injector;
        // TODO: destroy the injector when the scope goes away. Today every store provided here (and any
        //  `effect` / `resource` it owns) outlives its AngularJS scope, so e.g. a closed event form's
        //  `EventFormStore` keeps reacting to late bumps. Enable the `$destroy` hook below once each
        //  caller in `framework-components.js` and `s25-event-creation-form.js` has been checked for
        //  anything that reads its store after the scope is destroyed.
        // $scope.$on?.("$destroy", () => injector.destroy());
        return injector;
    }

    compileElement(
        element: Element,
        model?: any,
        outputs?: Record<string, (val: unknown) => void>,
        injector: Injector = this.injector,
    ): ComponentRef<unknown>[] {
        // here we loop though the factories, find the element based on the selector
        // let factories = (this.ngModRef.componentFactoryResolver as any)._factories;
        const componentRefs: ComponentRef<unknown>[] = [];

        for (const selector in this.typeMapService.typeMap) {
            let comp: ComponentMirror<any>;
            try {
                comp = S25Assert.definedValue(
                    reflectComponentType(this.typeMapService.typeMap[selector]),
                    `Could not match selector ${selector}. Do you need @TypeManagerDecorator?`,
                );
            } catch (e) {
                console.log(e.message);
                continue;
            }

            const list = Array.prototype.slice.call(element.querySelectorAll(comp.selector));
            element.matches(comp.selector) && list.unshift(element);
            if (!componentRefs.length) {
                list.forEach((item: Element) => {
                    const parent = item.parentNode!;
                    const next = item.nextSibling!;
                    const ngContentNodes: Node[][] = []; // this is for the viewchild/viewchildren of this object

                    comp.ngContentSelectors.forEach((sel: string) => {
                        const ngContentList: Node[] = [];

                        // all children;
                        if (sel === "*") {
                            item.childNodes.forEach((c: ChildNode) => {
                                ngContentList.push(c);
                            });
                        } else {
                            const selList = item.querySelectorAll(sel);
                            selList.forEach((l: Element) => {
                                ngContentList.push(l);
                            });
                        }

                        ngContentNodes.push(ngContentList);
                    });

                    // here is where we compile the factory based on the node we have
                    const component = createComponent(this.typeMapService.typeMap[selector], {
                        environmentInjector: this.environmentInjector,
                        elementInjector: injector,
                        projectableNodes: ngContentNodes,
                        hostElement: item,
                    });

                    if (model) {
                        // This should work for input signals and @Input properties, but
                        // we still keep the fallback just in case the behavior is brittle
                        Object.entries(model ?? {}).forEach(([key, value]) => {
                            try {
                                component.setInput(key, value);
                            } catch (e) {
                                console.warn(`setting non-input "${key}" onto ${comp.selector}`);
                            }
                        });
                        /**
                         * fall back to previous behavior for non-signal properties.
                         * this is redundant for any @Input() properties, but does not
                         * have any negative side effects unless you have an input alias
                         * and prop with same name, e.g.
                         * ```ts
                         * @Input({alias: "itemId"} itemIdInput: string | number;
                         * itemId: number;
                         * ```
                         */
                        jSith.forEach(model, (key: any, value: any) => {
                            if (!isSignal((component.instance as any)[key])) {
                                (component.instance as any)[key] = value;
                            }
                        });
                        model = null;
                    }
                    // invoke callbacks when component output fires
                    if (outputs) {
                        Object.entries(outputs ?? {}).forEach(<T>([key, binding]: [string, (val: T) => void]) => {
                            try {
                                const subscription = (<OutputRef<T> | EventEmitter<T>>(
                                    (component.instance as any)[key]
                                )).subscribe(binding);
                                component.onDestroy(() => subscription.unsubscribe());
                            } catch (e) {
                                throw new Error(
                                    `attempting to bind to missing component output "${key}" to ${comp.selector}`,
                                    e,
                                );
                            }
                        });
                    }

                    this.app.attachView(component.hostView);

                    // we need to move the newly compiled element, as it was appended to this components html
                    if (next) {
                        parent.insertBefore(component.location.nativeElement, next);
                    } else {
                        parent.appendChild(component.location.nativeElement);
                    }

                    component.changeDetectorRef && component.changeDetectorRef.detectChanges();
                    component.hostView.detectChanges && component.hostView.detectChanges(); // tell the component to detectchanges
                    componentRefs.push(component);
                });
            }
        }
        return componentRefs;
    }

    /**
     * Counterpart to the `attachView` in {@link compileElement}. Hosts that compile components into
     * their own markup must call this when that markup goes away, or `ApplicationRef` keeps change
     * detecting the orphaned views for the life of the tab.
     */
    destroyComponentRefs(componentRefs: ComponentRef<unknown>[] | null | undefined) {
        if (!componentRefs?.length) return;

        for (const componentRef of componentRefs) {
            if (!componentRef || componentRef.hostView.destroyed) continue;
            try {
                this.app.detachView(componentRef.hostView);
                componentRef.destroy();
            } catch (caughtError) {
                // one component's ngOnDestroy must not strand the rest of the refs
                console.warn("failed to destroy bridged component", caughtError);
            }
        }
    }

    compileHTML(html: string, target?: HTMLElement, model?: any, injector?: Injector) {
        target = target || document.body;

        const parentEl = document.createElement("div");
        parentEl.innerHTML = html;

        jSith.forEach(parentEl.children as unknown as Element[], (_: any, child: Element) => {
            target.appendChild(child);
            this.compileElement(child, model, undefined, injector);
        });
    }
}
